import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { fetchCertificationById } from '@/api/certifications';
import type { UpdateCertificationPayload } from '@/api/certifications/types';
import { CreateCertificationDialog } from '@/components/shared/CreateCertificationDialog';
import { useTable } from '@/hooks/useTable';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { useCertificationDraftStore } from '@/store/certificationDraftStore';
import { CertificationCommentsSection } from '../../-components/CertificationCommentsSection';
import { CertificationDetailHeader } from '../../-components/CertificationDetailHeader';
import { CertificationInfoCard } from '../../-components/CertificationInfoCard';
import { CertificationStylesSection } from '../../-components/CertificationStylesSection';
import { CertificationVersionHistory } from '../../-components/CertificationVersionHistory';
import { useArchiveCertification } from '../../-hooks/useArchiveCertification';
import {
  certificationDetailKeys,
  useCertificationDetail,
} from '../../-hooks/useCertificationDetail';
import {
  useCertificationVersionHistory,
  VERSION_HISTORY_PAGE_SIZE,
} from '../../-hooks/useCertificationVersionHistory';
import { useDeleteCertification } from '../../-hooks/useDeleteCertification';
import { useSubmitCertificationForReview } from '../../-hooks/useSubmitCertificationForReview';
import { useUnarchiveCertification } from '../../-hooks/useUnarchiveCertification';
import { useUpdateCertification } from '../../-hooks/useUpdateCertification';

export const Route = createFileRoute('/(client)/certifications/(pages)/$id/')({
  loader: ({ params }) =>
    queryClient.ensureQueryData({
      queryKey: certificationDetailKeys.detail(params.id),
      queryFn: () => fetchCertificationById(params.id),
    }),
  component: CertificationDetailPage,
});

const STYLES_TYPES = new Set(['PRODUCT', 'TRANSACTION']);

function CertificationDetailPage() {
  const { id } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'certificationDetail' });
  const { data: cert, isLoading } = useCertificationDetail(id);
  const updateMutation = useUpdateCertification(id);
  const submitForReviewMutation = useSubmitCertificationForReview(id);
  const deleteMutation = useDeleteCertification(id);
  const archiveMutation = useArchiveCertification(id);
  const unarchiveMutation = useUnarchiveCertification(id);
  const updateManualDraft = useCertificationDraftStore(
    s => s.updateManualDraft,
  );
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [renewDialogKey, setRenewDialogKey] = useState(0);
  const historyTable = useTable('certification-version-history', {
    mode: 'state',
    initialState: { page: 1, limit: VERSION_HISTORY_PAGE_SIZE },
  });
  const { data: historyData, isLoading: isHistoryLoading } =
    useCertificationVersionHistory(id, historyTable.page);

  if (isLoading || !cert) {
    return (
      <div className='flex flex-col flex-1 min-h-0 overflow-y-auto p-6 gap-4 animate-pulse'>
        <div className='h-24 rounded-lg bg-muted' />
        <div className='h-48 rounded-lg bg-muted' />
      </div>
    );
  }

  async function handleUpdate(patch: UpdateCertificationPayload) {
    if (!cert) return;
    const toIso = (v: string | null | undefined) =>
      v ? new Date(v).toISOString() : null;
    const strOrNull = (v: string | null | undefined) => v || null;

    const payload: UpdateCertificationPayload = {
      expirationDate: toIso(
        'expirationDate' in patch ? patch.expirationDate : cert.expirationDate,
      ),
      issueDate: toIso('issueDate' in patch ? patch.issueDate : cert.issueDate),
      documentNumber: strOrNull(
        'documentNumber' in patch ? patch.documentNumber : cert.documentNumber,
      ),
      licenseNumber: strOrNull(
        'licenseNumber' in patch ? patch.licenseNumber : cert.licenseNumber,
      ),
      issuedBy: strOrNull('issuedBy' in patch ? patch.issuedBy : cert.issuedBy),
      explanation: strOrNull(
        'explanation' in patch ? patch.explanation : cert.explanation,
      ),
      ...('fileId' in patch ? { fileId: patch.fileId ?? null } : {}),
      ...('styleIds' in patch
        ? {
            styleIds: patch.styleIds,
            shouldAttachChildren: patch.shouldAttachChildren ?? false,
          }
        : {}),
      ...('title' in patch ? { title: strOrNull(patch.title) } : {}),
      ...('featuredImageFileId' in patch
        ? { featuredImageFileId: patch.featuredImageFileId ?? null }
        : {}),
      ...('minimumWage' in patch
        ? { minimumWage: strOrNull(patch.minimumWage) }
        : {}),
      ...('livingWage' in patch
        ? { livingWage: strOrNull(patch.livingWage) }
        : {}),
    };
    try {
      await updateMutation.mutateAsync(payload);
      toast.success(t('certificationDetail.updateSuccess'));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t('certificationDetail.updateError'),
      );
    }
  }

  async function handleSubmitForReview() {
    try {
      await submitForReviewMutation.mutateAsync();
      toast.success(t('certificationDetail.updateSuccess'));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t('certificationDetail.updateError'),
      );
    }
  }

  async function handleDelete() {
    try {
      await deleteMutation.mutateAsync();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t('certificationDetail.deleteError'),
      );
    }
  }

  async function handleArchive() {
    if (!cert) return;
    const isArchived = Boolean(cert.isArchived);
    try {
      if (isArchived) {
        await unarchiveMutation.mutateAsync();
      } else {
        await archiveMutation.mutateAsync();
      }
      toast.success(
        isArchived
          ? t('certificationDetail.unarchiveSuccess')
          : t('certificationDetail.archiveSuccess'),
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t('certificationDetail.archiveError'),
      );
    }
  }

  function handleViewFile(fileUrl?: string) {
    const url = fileUrl ?? cert?.fileUrl;
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  function handleRenew() {
    if (!cert) return;
    updateManualDraft(cert.companyId, {
      step: 2,
      standardId: cert.standardId,
      selectedExistingCertId: cert.id,
    });
    setRenewDialogKey(k => k + 1);
    setRenewDialogOpen(true);
  }

  const hasStylesSection = cert.type ? STYLES_TYPES.has(cert.type) : false;

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
      <CreateCertificationDialog
        key={renewDialogKey}
        open={renewDialogOpen}
        companyId={cert.companyId}
        companyName={cert.companyName ?? cert.companyId}
        onClose={() => setRenewDialogOpen(false)}
      />

      <CertificationDetailHeader
        cert={cert}
        onViewFile={() => handleViewFile()}
        onRenew={handleRenew}
        onSubmitForReview={handleSubmitForReview}
        onDelete={handleDelete}
        onArchive={handleArchive}
        isViewingFile={false}
        isSubmittingForReview={submitForReviewMutation.isPending}
        isDeletingCertification={deleteMutation.isPending}
        isArchivingCertification={
          archiveMutation.isPending || unarchiveMutation.isPending
        }
        t={t}
      />

      <div className='flex flex-col gap-6 p-4 @md:p-6'>
        <div className='flex flex-col gap-3'>
          <div className='grid grid-cols-1 @md:grid-cols-2 gap-4 items-stretch'>
            <CertificationInfoCard
              cert={cert}
              isPending={updateMutation.isPending}
              onUpdate={handleUpdate}
              onViewFile={() => handleViewFile()}
              isViewingFile={false}
              isArchived={cert.isArchived ?? false}
              t={t}
              className='h-full'
            />
            <CertificationCommentsSection
              certId={cert.id}
              companyId={cert.companyId}
              isArchived={cert.isArchived ?? false}
            />
          </div>

          {hasStylesSection && (
            <CertificationStylesSection
              certId={cert.id}
              companyId={cert.companyId}
              standardId={cert.standardId}
              issueDate={cert.issueDate}
              expirationDate={cert.expirationDate}
              isPending={updateMutation.isPending}
              onSave={handleUpdate}
              isArchived={cert.isArchived ?? false}
              t={t}
            />
          )}
        </div>

        <CertificationVersionHistory
          rows={historyData?.data ?? []}
          total={historyData?.meta.totalCount ?? 0}
          page={historyTable.page}
          pageSize={VERSION_HISTORY_PAGE_SIZE}
          isLoading={isHistoryLoading}
          onPageChange={p =>
            historyTable.onPaginationChange({
              pageIndex: p - 1,
              pageSize: VERSION_HISTORY_PAGE_SIZE,
            })
          }
          onViewFile={handleViewFile}
          t={t}
        />
      </div>
    </div>
  );
}
